import { JABAZ_API_URL } from './constants';
import type { JabazDoctor, JabazMembership } from './types';

/**
 * Client of «دبّه», the queue service (repo Mohammadi/dabbeh). Off while JABAZ_API_URL is empty: the
 * queue then lives on this device only, as the prototype always did. Every call carries the patient's
 * login cookie (`credentials: include`); the service verifies it like hesarak does.
 */
export const isQueueApi = () => !!JABAZ_API_URL;

export interface ServerOffer {
  id: number;
  request_code: string;
  visit_at: string | null;
  visit_label: string | null;
  hold_expires_at: string;
  center_id: string;
  service_id: string;
  server_id: string;
  user_center_id: string;
}

export interface ServerSeat {
  seat_id: number;
  slug: string;
  queue_id: number;
  position: number;
  status: 'waiting' | 'offered' | 'missed';
  tracking_code: string;
  joined_at: string;
  offer: ServerOffer | null;
}

export class QueueApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | undefined,
    message: string,
  ) {
    super(message);
  }
}

const call = async <T>(path: string, init: Parameters<typeof fetch>[1] = {}): Promise<T> => {
  // trailing slash: the site has trailingSlash:true, which would answer every call with a redirect first
  const response = await fetch(`${JABAZ_API_URL}${path.endsWith('/') ? path : `${path}/`}`, {
    ...init,
    credentials: 'include',
    headers: { ...(init?.body ? { 'content-type': 'application/json' } : {}), ...(init?.headers as Record<string, string>) },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new QueueApiError(response.status, body?.code, body?.message || 'ارتباط با صف برقرار نشد؛ دوباره تلاش کنید.');
  return body as T;
};

const slugPath = (slug: string) => encodeURIComponent(slug);

export const queueApi = {
  summary: (slug: string) => call<{ enabled: boolean; waiting?: number; hold_minutes?: number }>(`/v1/queues/${slugPath(slug)}`),
  mySeats: () => call<{ seats: ServerSeat[] }>('/v1/me/seats').then(body => body.seats),
  join: (slug: string) => call<{ seat: ServerSeat }>(`/v1/queues/${slugPath(slug)}/seats`, { method: 'POST' }).then(body => body.seat),
  leave: (slug: string) => call<{ left: boolean }>(`/v1/queues/${slugPath(slug)}/seats/me`, { method: 'DELETE' }),
  pass: (offerId: number) => call<{ seat: ServerSeat }>(`/v1/offers/${offerId}/pass`, { method: 'POST' }).then(body => body.seat),
  refresh: (offerId: number) => call<{ seat: ServerSeat }>(`/v1/offers/${offerId}/refresh`, { method: 'POST' }).then(body => body.seat),
  booked: (offerId: number, bookId: string) =>
    call<{ booked: boolean }>(`/v1/offers/${offerId}/booked`, { method: 'POST', body: JSON.stringify({ book_id: bookId }) }),
};

/** The service's seat in the store's shape; doctor details stay those the patient joined from. */
export const toMembership = (seat: ServerSeat, doctor: JabazDoctor, viaSubscription = false): JabazMembership => ({
  doctor,
  joinedAt: seat.joined_at,
  position: seat.position,
  trackingCode: seat.tracking_code,
  status: seat.status,
  viaSubscription,
  seatId: seat.seat_id,
  offer: seat.offer
    ? {
        id: seat.offer.id,
        visitAt: seat.offer.visit_at ?? seat.offer.hold_expires_at,
        visitLabel: seat.offer.visit_label ?? undefined,
        holdExpiresAt: seat.offer.hold_expires_at,
        centerId: seat.offer.center_id,
        serviceId: seat.offer.service_id,
        serverId: seat.offer.server_id,
        userCenterId: seat.offer.user_center_id,
        requestCode: seat.offer.request_code,
      }
    : undefined,
});
